import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getProducts } from '../api/api';
import ProductCard from '../components/ProductCard';
import { Filter, X } from 'lucide-react';

const Products = () => {
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [showFilters, setShowFilters] = useState(false);

  const categories = [
    { id: 'all', name: 'Tümü' },
    { id: 'JUNTO® Kahveler', name: 'JUNTO® Kahveler' },
    { id: 'JUNTO® Sıcak Toz Ürünler', name: 'JUNTO® Sıcak Toz Ürünler' },
    { id: 'Espresso Makineleri', name: 'Espresso Makineleri' },
    { id: 'JUNTO® Çaylar', name: 'JUNTO® Çaylar' },
  ];

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    // URL parametresinden kategoriyi al
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [location.search]);

  useEffect(() => {
    filterAndSortProducts();
  }, [products, selectedCategory, sortBy]);

  const loadProducts = async (isRetry = false) => {
    try {
      if (isRetry) {
        setRetrying(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const data = await getProducts();
      setProducts(data);
      setError(null);
    } catch (error) {
      console.error('Error loading products:', error);
      setError('Ürünler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  };

  const filterAndSortProducts = () => {
    let filtered = [...products];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    // Sort
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'featured':
      default:
        filtered.sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
        break;
    }

    setFilteredProducts(filtered);
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen pt-44 pb-16 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-junto-red mx-auto mb-4"></div>
          <p className="text-xl text-gray-600">Ürünler yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen pt-44 pb-16 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8">
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Bir Hata Oluştu</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => loadProducts(true)}
              disabled={retrying}
              className="bg-junto-red hover:bg-junto-red-dark text-white font-bold py-3 px-8 rounded-xl transition-colors disabled:opacity-50"
            >
              {retrying ? 'Yeniden Deneniyor...' : 'Tekrar Dene'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-44 pb-16">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-12 text-center" data-testid="products-header">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Ürünlerimiz
          </h1>
          <p className="text-xl text-gray-600">
            {filteredProducts.length} ürün bulundu
          </p>
        </div>

        {/* Filters */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            {/* Category Filters - Desktop */}
            <div className="hidden md:flex flex-wrap gap-2" data-testid="category-filters">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    selectedCategory === cat.id
                      ? 'bg-gradient-to-r from-junto-red to-junto-red-dark text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-red-50 border border-gray-200'
                  }`}
                  data-testid={`category-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Mobile Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center space-x-2 bg-white px-4 py-2 rounded-lg border border-gray-200"
              data-testid="mobile-filter-toggle"
            >
              <Filter className="w-5 h-5" />
              <span>Filtrele</span>
            </button>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-junto-red"
              data-testid="sort-select"
            >
              <option value="featured">Öne Çıkanlar</option>
              <option value="price-low">Fiyat: Düşükten Yükseğe</option>
              <option value="price-high">Fiyat: Yüksekten Düşüğe</option>
              <option value="rating">En Yüksek Puan</option>
            </select>
          </div>

          {/* Mobile Category Filters */}
          {showFilters && (
            <div className="md:hidden mt-4 bg-white rounded-lg shadow-lg p-4 fade-in" data-testid="mobile-category-filters">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Kategoriler</h3>
                <button onClick={() => setShowFilters(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setSelectedCategory(cat.id);
                      setShowFilters(false);
                    }}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-all ${
                      selectedCategory === cat.id
                        ? 'bg-junto-red text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-red-50'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20" data-testid="loading-indicator">
            <div className="loading-coffee">
              <div></div>
              <div></div>
            </div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20" data-testid="no-products">
            <p className="text-xl text-gray-600">Bu kategoride ürün bulunamadı.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="products-grid">
            {filteredProducts.map((product, index) => (
              <div key={product.id} className="fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;